import { Reference } from '@apollo/client';
import { getStoreKeyName } from '@apollo/client/utilities';
import { useCallback } from 'react';
import { toast } from 'react-hot-toast';
import {
  CommunityReplyFragmentDoc,
  useAddCommunityReplyMutation,
} from 'generated/graphql';
import { CommunityId } from '../model/community.types';
import { PostId } from '../model/post.types';
import { ReplyId } from '../model/reply.types';
import { createOptimisticReply } from '../lib/createOptimisticReply';
import { modifyPostCommentCount } from '../lib/modifyPostCommentCount';
import { useCommunityCreatorIdentity } from './useCommunityCreatorIdentity';

const REPLIES_STORE_KEY = getStoreKeyName('replies', {
  direction: undefined,
  lastId: undefined,
});

// The mutation selects only the fields the create resolver populates; the
// server rejects isSensitiveContent/reactions on a freshly created reply, so
// the cache entry merges the returned fields with new-reply defaults.
export const useAddReply = (
  communityId: CommunityId,
  postId: PostId,
  canModerate: boolean,
  influencerId?: string,
) => {
  const [addReplyMutation] = useAddCommunityReplyMutation();
  const identity = useCommunityCreatorIdentity(influencerId);

  return useCallback(
    (text: string, parentReplyId?: ReplyId): Promise<boolean> => {
      const optimistic = createOptimisticReply(
        text,
        identity,
        parentReplyId ?? postId,
      );

      return addReplyMutation({
        variables: {
          communityId,
          postId,
          text,
          parentReplyId: parentReplyId ?? null,
        },
        optimisticResponse: {
          __typename: 'Mutation',
          addReply: {
            __typename: 'Reply',
            id: optimistic.id,
            text: optimistic.text,
            createdAt: optimistic.createdAt,
            updatedAt: null,
            parentId: optimistic.parentId,
            author: {
              __typename: 'Author',
              id: optimistic.author.id,
              username: optimistic.author.username,
              badges: optimistic.author.badges,
              profilePicture: optimistic.author.profilePicture,
            },
          },
        },
        update: (cache, { data }) => {
          const reply = data?.addReply;
          if (!reply) return;

          const ref = cache.writeFragment({
            fragment: CommunityReplyFragmentDoc,
            fragmentName: 'CommunityReply',
            variables: { canModerate },
            data: {
              ...optimistic,
              id: reply.id,
              text: reply.text,
              createdAt: reply.createdAt,
              parentId: reply.parentId ?? null,
              author: {
                ...optimistic.author,
                id: reply.author.id,
                username: reply.author.username,
                badges: reply.author.badges,
                profilePicture: reply.author.profilePicture,
              },
            },
          });

          cache.modify({
            id: cache.identify({ __typename: 'Post', id: postId }),
            fields: {
              replies: (
                existing: readonly Reference[] = [],
                { storeFieldName, readField },
              ) => {
                if (storeFieldName !== REPLIES_STORE_KEY || !ref) {
                  return existing;
                }
                const alreadyListed = existing.some(
                  (item) => readField('id', item) === reply.id,
                );
                return alreadyListed ? existing : [ref, ...existing];
              },
            },
          });
          modifyPostCommentCount(cache, postId, 1);
        },
      })
        .then(() => {
          toast.success('Reply added successfully!');
          return true;
        })
        .catch(() => {
          toast.error('Something went wrong');
          return false;
        });
    },
    [addReplyMutation, communityId, postId, canModerate, identity],
  );
};
